import { rewriteCss, unrewriteCss } from "@rewriters/css";
import { unrewriteUrl } from "@rewriters/url";
import { ScramjetClient } from "@client/index";
import { Reflect_apply, Reflect_get, Reflect_set } from "@/shared/snapshot";

export default function (client: ScramjetClient) {
	client.Proxy("CSSStyleDeclaration.prototype.setProperty", {
		apply(ctx) {
			if (!ctx.args[1]) return;
			ctx.args[1] = rewriteCss(ctx.args[1], client.context, client.meta);
		},
	});

	client.Proxy("CSSStyleDeclaration.prototype.getPropertyValue", {
		apply(ctx) {
			const v = ctx.call();
			if (!v) return v;
			ctx.return(unrewriteCss(v, client.context));
		},
	});

	client.Trap("CSSStyleDeclaration.prototype.cssText", {
		set(ctx, value: string) {
			ctx.set(rewriteCss(value, client.context, client.meta));
		},
		get(ctx) {
			return unrewriteCss(ctx.get(), client.context);
		},
	});

	// A style sheet's address as the page wrote it, like the href of the <link> it came from.
	// require-css (RequireJS sites such as CryptPad) waits for document.styleSheets to hold a
	// sheet whose href equals the link's, and waited forever on the proxied one.
	for (const path of ["StyleSheet.prototype.href", "CSSImportRule.prototype.href"]) {
		client.Trap(path, {
			get(ctx) {
				const href = ctx.get() as string | null;

				return href ? unrewriteUrl(href, client.context) : href;
			},
		});
	}

	client.Proxy("CSSStyleSheet.prototype.insertRule", {
		apply(ctx) {
			ctx.args[0] = rewriteCss(ctx.args[0], client.context, client.meta);
		},
	});

	client.Proxy("CSSStyleSheet.prototype.replace", {
		apply(ctx) {
			ctx.args[0] = rewriteCss(ctx.args[0], client.context, client.meta);
		},
	});

	client.Proxy("CSSStyleSheet.prototype.replaceSync", {
		apply(ctx) {
			ctx.args[0] = rewriteCss(ctx.args[0], client.context, client.meta);
		},
	});

	client.Trap("CSSRule.prototype.cssText", {
		set(ctx, value: string) {
			ctx.set(rewriteCss(value, client.context, client.meta));
		},
		get(ctx) {
			return unrewriteCss(ctx.get(), client.context);
		},
	});

	client.Proxy("CSSStyleValue.parse", {
		apply(ctx) {
			if (!ctx.args[1]) return;
			ctx.args[1] = rewriteCss(ctx.args[1], client.context, client.meta);
		},
	});

	// A style declaration whose values read back with the page's own addresses. The values a
	// browser gives are lower-case url(), so values without one need no work.
	const pageStyle = (style: CSSStyleDeclaration) =>
		new Proxy(style, {
			get(target, prop) {
				const value = Reflect_get(target, prop);

				if (typeof value === "function") {
					return new Proxy(value, {
						apply(target, that, args) {
							return Reflect_apply(target, style, args);
						},
					});
				}

				if (prop in CSSStyleDeclaration.prototype) return value;
				if (!value || typeof value !== "string" || !value.includes("url("))
					return value;

				return unrewriteCss(value, client.context);
			},
			set(target, prop, value) {
				if (prop == "cssText" || value == "" || typeof value !== "string") {
					return Reflect_set(target, prop, value);
				}

				return Reflect_set(
					target,
					prop,
					rewriteCss(value, client.context, client.meta)
				);
			},
		});

	// Computed styles too: pages copy url() values from them into other elements (Amazon), and
	// the proxied address then went through the rewriting a second time.
	client.Proxy("getComputedStyle", {
		apply(ctx) {
			const style = ctx.call();
			if (style) ctx.return(pageStyle(style));
		},
	});

	client.Trap("HTMLElement.prototype.style", {
		get(ctx) {
			// unfortunate and dumb hack. we have to trap every property of this
			// since the prototype chain is fucked
			return pageStyle(ctx.get() as CSSStyleDeclaration);
		},
		set(ctx, value: string) {
			// this will actually run the trap for cssText. don't rewrite it here
			ctx.set(value);
		},
	});
}
